import * as React from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { useTheme } from '../../../../../theme';
import { BaseButton, Checkbox } from '../../../../../BaseComponents';
import { CheckoutOrderFragment, ExareStatus } from '@websky/graphql';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useState } from 'react';
import InfoLinksLabel from '../../../../../InfoLinksLabel';

interface Props {
	className?: string;
	type: 'refund' | 'exchange';
	status: ExareStatus;
	ns?: string;
	order?: CheckoutOrderFragment;
	onConfirm?: () => void;
	onDelete?: () => void;
	onCreateNewRequest?: () => void;
}

const ExchangeRefundRequestControls: React.FC<Props> = ({
	className,
	type,
	status,
	ns = 'Refund',
	order,
	onConfirm,
	onDelete,
	onCreateNewRequest
}) => {
	const { t } = useTranslation(ns);

	const { ExchangeRefundRequestControls: theme } = useTheme('Entities').Order;

	const [activeCheckbox, setActiveCheckbox] = useState(false);

	const showConfirmButton = Boolean(
		onConfirm && [ExareStatus.AwaitingPayment, ExareStatus.NeedConfirm].includes(status)
	);

	const showDeleteButton = Boolean(
		onDelete && [ExareStatus.InProcess, ExareStatus.AwaitingPayment, ExareStatus.NeedConfirm].includes(status)
	);

	const showCreateNewRequestButton = Boolean(
		onCreateNewRequest && [ExareStatus.Refused, ExareStatus.Rejected, ExareStatus.TimelimitExpired].includes(status)
	);

	const showInfoText = status === ExareStatus.InProcess && showDeleteButton;

	const showInfoLinks = Boolean([ExareStatus.AwaitingPayment, ExareStatus.NeedConfirm].includes(status));

	const disabledButton = showInfoLinks && !activeCheckbox;

	return (
		<div className={cn(theme.root, className)}>
			<div>
				{showInfoText && <span className={theme.text}>{t('ControlsRequest.InfoText')}</span>}

				{showInfoLinks && (
					<FormControlLabel
						className={theme.links}
						checked={activeCheckbox}
						onChange={(_, value) => setActiveCheckbox(value)}
						control={<Checkbox classes={{ root: theme.checkbox, checked: theme.active }} />}
						label={<InfoLinksLabel type={type} order={order} />}
					/>
				)}
			</div>

			<div className={theme.buttons}>
				{showDeleteButton && (
					<BaseButton className={theme.deleteButton} variant="secondary" onClick={onDelete} fullWidth>
						{t('ControlsRequest.Delete')}
					</BaseButton>
				)}

				{showConfirmButton && (
					<BaseButton variant="action" onClick={onConfirm} disabled={disabledButton} fullWidth>
						{t('ControlsRequest.Confirm')}
					</BaseButton>
				)}

				{showCreateNewRequestButton && (
					<BaseButton variant="action" onClick={onCreateNewRequest} disabled={disabledButton} fullWidth>
						{t('ControlsRequest.CreateNew')}
					</BaseButton>
				)}
			</div>
		</div>
	);
};

export default ExchangeRefundRequestControls;
